import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import type { UploadUrlInput } from '@proref/schemas';
import { useAuth } from '../../contexts/auth-context';
import { useFederationsQuery } from '../../hooks/use-federations';
import { useSportsQuery } from '../../hooks/use-sports';
import { invalidateClipCaches } from '../../hooks/use-clips';
import {
  createUploadUrl,
  uploadToS3,
  markUploadComplete,
  deleteClip,
} from '../../services/clips.service';
import { Modal } from '../../components/shared/Modal';
import { Field } from '../../components/shared/Field';
import { Select } from '../../components/shared/Select';
import { Button } from '../../components/shared/Button';
import { Spinner } from '../../components/shared/Spinner';
import { Icon } from '../../components/shared/Icons';
import { notifyError, notifySuccess, notifyWarning } from '../../lib/toast';
import { probeVideo } from '../../lib/video-probe';
import {
  MAX_CLIP_MB,
  MAX_CLIP_MIN,
  describeFileProblem,
  formatDuration,
  titleFromFilename,
} from '../../lib/clip-file';

// Files per batch. Each one spends a call on /media/upload-url AND one on /uploads/:id/complete,
// both rate-limited at 60/min, so 20 files (40 calls) leaves room for the list's own polling —
// and 20 × up to 500 MB is already a long sitting.
const MAX_BATCH = 20;

// Uploads running at once. Browsers cap concurrent connections to a host at ~6, and these are
// large PUTs: more in flight just makes every one of them slower while starving the API calls
// interleaved between them. Three keeps the pipe full without that.
const CONCURRENCY = 3;

// Sentinel for the super_admin federation picker meaning "Official (all federations)" — chosen, so
// it satisfies the "must pick something" guard, but it omits federation_id → a platform clip.
const OFFICIAL_VALUE = 'official';

type ItemStatus = 'checking' | 'ready' | 'rejected' | 'uploading' | 'done' | 'failed';

interface UploadItem {
  id: string;
  file: File;
  status: ItemStatus;
  // Set on 'rejected' (the file can't be sent) or 'failed' (the send didn't work).
  problem?: string;
  durationSec?: number | null;
}

interface BulkUploadClipsDialogProps {
  open: boolean;
  onClose: () => void;
}

// Upload many clips in one sitting. Deliberately NOT the single-clip wizard in a loop: that flow's
// payoff is waiting out the transcode and being dropped into the editor, which makes no sense for
// twenty files. Here the browser's job ends when the bytes land — MediaConvert is triggered by the
// S3 event, and the clips list polls each row's status on its own — so the batch finishes as soon
// as the last PUT resolves and the admin tags them from the list afterwards.
//
// The per-clip authoring (decision point, rule, correct call, options) genuinely can't be batched;
// this is about getting the FILES in, which is the part that was serial busywork.
export function BulkUploadClipsDialog({ open, onClose }: BulkUploadClipsDialogProps) {
  const { t } = useTranslation();
  const { role } = useAuth();
  const isSuper = role === 'super_admin';
  const qc = useQueryClient();

  const federations = useFederationsQuery(isSuper && open);
  const sports = useSportsQuery(isSuper && open);

  const [items, setItems] = useState<UploadItem[]>([]);
  const [federationId, setFederationId] = useState('');
  const [sportId, setSportId] = useState('');
  const [running, setRunning] = useState(false);
  const [finished, setFinished] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // The component stays mounted across open/close, so a re-open would otherwise resume the last
  // batch's state.
  useEffect(() => {
    if (open) return;
    setItems([]);
    setFederationId('');
    setSportId('');
    setRunning(false);
    setFinished(false);
  }, [open]);

  function patch(id: string, next: Partial<UploadItem>) {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...next } : it)));
  }

  // Probe each file as it's added, not at upload time: an over-long or oversized clip is called out
  // immediately instead of after waiting out a PUT that was always going to be truncated.
  async function addFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    const incoming = Array.from(files);

    setItems((prev) => {
      const room = MAX_BATCH - prev.length;
      if (room <= 0) {
        notifyWarning(t('admin.clips.bulkUploadDialog.tooManyFilesToast', { max: MAX_BATCH }));
        return prev;
      }
      if (incoming.length > room) {
        notifyWarning(t('admin.clips.bulkUploadDialog.partialAddToast', { room, max: MAX_BATCH }));
      }
      const added = incoming.slice(0, room).map((file, i) => ({
        // File objects have no id and names repeat across folders, so mint one.
        id: `${Date.now()}-${i}-${file.name}`,
        file,
        status: 'checking' as ItemStatus,
      }));
      // Probe outside the state updater (it's async and this must stay pure).
      queueMicrotask(() => {
        for (const item of added) void checkOne(item);
      });
      return [...prev, ...added];
    });

    // Let the same file be picked again after being removed.
    if (fileRef.current) fileRef.current.value = '';
  }

  async function checkOne(item: UploadItem) {
    const probe = await probeVideo(item.file);
    const problem = describeFileProblem(item.file, probe, t);
    patch(item.id, {
      status: problem ? 'rejected' : 'ready',
      ...(problem ? { problem } : {}),
      durationSec: probe?.durationSec ?? null,
    });
  }

  function remove(id: string) {
    setItems((prev) => prev.filter((it) => it.id !== id));
  }

  // One file's full chain: presign (which creates the draft row) → PUT → mark complete.
  async function uploadOne(item: UploadItem, payloadBase: Omit<UploadUrlInput, 'title' | 'size_bytes'>) {
    patch(item.id, { status: 'uploading' });
    // Held locally because the draft only exists from the presign onward, and a failure after
    // that point leaves a row stranded in `uploading` that nothing will ever transition.
    let draftId: string | null = null;
    try {
      const { clip, upload_url } = await createUploadUrl({
        ...payloadBase,
        title: titleFromFilename(item.file.name, t),
        size_bytes: item.file.size,
      });
      draftId = clip.id;
      await uploadToS3(upload_url, item.file);
      patch(item.id, { status: 'done' });
      // Not awaited into the failure path: the upload SUCCEEDED, and a failed status ping must
      // never turn it into a failed row — worst case the chip reads "Uploading" until the
      // transcoding webhook lands.
      void markUploadComplete(draftId).catch(() => {});
    } catch (err) {
      patch(item.id, {
        status: 'failed',
        problem: err instanceof Error ? err.message : t('admin.clips.bulkUploadDialog.uploadFailedFallback'),
      });
      // Roll the draft back so it doesn't sit in the clips list forever as an in-flight upload
      // that can never complete (same reasoning as the single-clip wizard's discard).
      if (draftId) await deleteClip(draftId).catch(() => {});
    }
  }

  async function start() {
    const queue = items.filter((it) => it.status === 'ready');
    if (queue.length === 0) return;
    if (isSuper && (!federationId || !sportId)) {
      notifyError(new Error('Missing tenancy'), t('admin.clips.uploadDialog.missingTenancyToast'));
      return;
    }

    const payloadBase = {
      content_type: 'video/mp4' as const,
      // super_admin names the sport, and EITHER a federation OR "Official" (the sentinel) —
      // Official omits federation_id, so the backend files these as platform clips.
      ...(isSuper && {
        sport_id: sportId,
        ...(federationId !== OFFICIAL_VALUE && { federation_id: federationId }),
      }),
    } as Omit<UploadUrlInput, 'title' | 'size_bytes'>;

    setRunning(true);
    // A shared cursor over the queue: CONCURRENCY workers each take the next file when they
    // finish one, which keeps exactly that many uploads in flight rather than uploading in
    // fixed chunks where the whole group waits on its slowest member.
    let cursor = 0;
    const worker = async () => {
      while (cursor < queue.length) {
        const item = queue[cursor++];
        if (item) await uploadOne(item, payloadBase);
      }
    };
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, queue.length) }, worker));

    setRunning(false);
    setFinished(true);
    invalidateClipCaches(qc);
  }

  const ready = items.filter((it) => it.status === 'ready').length;
  const done = items.filter((it) => it.status === 'done').length;
  const failed = items.filter((it) => it.status === 'failed').length;
  const checking = items.some((it) => it.status === 'checking');

  // Report the outcome once, when the run ends — per-file toasts would be a wall of them.
  useEffect(() => {
    if (!finished) return;
    if (failed === 0) notifySuccess(t('admin.clips.bulkUploadDialog.finishedSuccessToast', { count: done }));
    else notifyWarning(t('admin.clips.bulkUploadDialog.finishedPartialToast', { done, failed }));
  }, [finished, done, failed, t]);

  return (
    <Modal
      open={open}
      onClose={running ? () => {} : onClose}
      title={t('admin.clips.bulkUploadDialog.title')}
      sub={
        finished
          ? t('admin.clips.bulkUploadDialog.subFinished')
          : t('admin.clips.bulkUploadDialog.subActive', { max: MAX_BATCH })
      }
      panelClass="max-w-2xl"
      footer={
        finished ? (
          <Button variant="accent" onClick={onClose}>
            {t('admin.clips.bulkUploadDialog.done')}
          </Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={running}>
              {t('common.cancel')}
            </Button>
            <Button variant="accent" onClick={start} disabled={running || ready === 0 || checking}>
              {running ? (
                <>
                  <Spinner className="w-3.5 h-3.5" />
                  {t('admin.clips.bulkUploadDialog.uploadingProgress', { current: done + 1, total: ready })}
                </>
              ) : ready === 0 ? (
                t('admin.clips.bulkUploadDialog.uploadButtonEmpty')
              ) : (
                t('admin.clips.bulkUploadDialog.uploadButton', { count: ready })
              )}
            </Button>
          </>
        )
      }
    >
      <div className="flex flex-col gap-3.5">
        {!finished && (
          <Field
            label={t('admin.clips.bulkUploadDialog.filesLabel', { maxMin: MAX_CLIP_MIN, maxMb: MAX_CLIP_MB })}
            htmlFor="bulk-clip-files"
          >
            <input
              ref={fileRef}
              id="bulk-clip-files"
              type="file"
              accept="video/mp4"
              multiple
              disabled={running}
              onChange={(e) => void addFiles(e.target.files)}
              className="block w-full text-[13px] text-ink-2 file:mr-3 file:rounded-sm file:border file:border-line-2 file:bg-surface file:px-3 file:py-1.5 file:text-[13px] file:text-ink"
            />
          </Field>
        )}

        {isSuper && !finished && (
          <div className="grid gap-3.5 sm:grid-cols-2">
            <Field label={t('admin.clips.uploadDialog.federationLabel')} htmlFor="bulk-clip-federation">
              <Select
                id="bulk-clip-federation"
                value={federationId}
                disabled={running}
                onChange={(e) => setFederationId(e.target.value)}
              >
                <option value="">{t('admin.clips.uploadDialog.chooseEllipsis')}</option>
                <option value={OFFICIAL_VALUE}>{t('admin.clips.bulkUploadDialog.officialAllFederationsParen')}</option>
                {(federations.data ?? []).map((fed) => (
                  <option key={fed.id} value={fed.id}>{fed.name}</option>
                ))}
              </Select>
            </Field>
            <Field label={t('admin.clips.uploadDialog.sportLabel')} htmlFor="bulk-clip-sport">
              <Select
                id="bulk-clip-sport"
                value={sportId}
                disabled={running}
                onChange={(e) => setSportId(e.target.value)}
              >
                <option value="">{t('admin.clips.uploadDialog.chooseEllipsis')}</option>
                {(sports.data ?? []).map((sp) => (
                  <option key={sp.id} value={sp.id}>{sp.name}</option>
                ))}
              </Select>
            </Field>
          </div>
        )}

        {items.length > 0 && (
          // `overflow-auto` rather than -y-auto: inside a phone-width modal the per-file table
          // needs the horizontal axis too (filename + size + progress + status).
          <div className="max-h-72 overflow-auto rounded border border-line">
            <table className="w-full min-w-[440px] text-left text-[12.5px]">
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} className="border-b border-line last:border-0">
                    <td className="px-3 py-1.5">
                      <div className="truncate font-medium text-ink">{titleFromFilename(it.file.name, t)}</div>
                      <div className="text-[11px] text-muted">
                        {(it.file.size / 1024 / 1024).toFixed(0)} MB
                        {it.durationSec != null && ` · ${formatDuration(it.durationSec)}`}
                      </div>
                    </td>
                    <td className="px-3 py-1.5 text-right text-[11px]">
                      <ItemState item={it} />
                    </td>
                    <td className="w-8 px-2 py-1.5 text-right">
                      {/* Removable until the batch starts — after that the row is a record of
                          what happened, not an editable queue. */}
                      {!running && !finished && (
                        <Button
                          variant="ghost"
                          size="sm"
                          icon
                          aria-label={t('admin.clips.bulkUploadDialog.removeAria', { name: it.file.name })}
                          onClick={() => remove(it.id)}
                        >
                          <Icon.close className="h-3 w-3" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Modal>
  );
}

function ItemState({ item }: { item: UploadItem }) {
  const { t } = useTranslation();
  switch (item.status) {
    case 'checking':
      return <span className="text-muted">{t('admin.clips.bulkUploadDialog.checking')}</span>;
    case 'ready':
      return <span className="text-muted">{t('admin.clips.bulkUploadDialog.ready')}</span>;
    case 'rejected':
      return <span className="text-danger">{item.problem}</span>;
    case 'uploading':
      return <span className="text-accent">{t('admin.clips.bulkUploadDialog.uploading')}</span>;
    case 'done':
      // The browser's part is over; MediaConvert takes it from here and the list polls it.
      return <span className="text-success">{t('admin.clips.bulkUploadDialog.doneStatus')}</span>;
    case 'failed':
      return <span className="text-danger">{item.problem ?? t('admin.clips.bulkUploadDialog.itemFailed')}</span>;
  }
}
